import { ref, watch } from 'vue'
import { useVueFlow } from '@vue-flow/core'

function getDefaultConfig(type) {
    switch (type) {
        case 'trigger':
            return { trigger_type: 'contact_created', trigger_config: {} }
        case 'action_email':
            return { template_id: null }
        case 'action_push':
            return { title: '', body: '', action_url: '' }
        case 'action_add_tag':
            return { tag_id: null, tag_name: '' }
        case 'condition':
            return { operator: 'AND', rules: [], true_branch: 'yes', false_branch: 'else' }
        case 'delay':
            return { amount: 1, unit: 'hours' }
        case 'wait_until':
            return { event_type: '', timeout_hours: 24 }
        case 'wait_for_date':
            return { date_field: '' }
        case 'split':
            return { paths: [{ id: 'a', label: 'A', percentage: 50 }, { id: 'b', label: 'B', percentage: 50 }] }
        case 'goal_check':
            return { operator: 'AND', rules: [], label: 'Goal' }
        default:
            return {}
    }
}

let nodeCounter = 0
function generateNodeId(type) {
    nodeCounter++
    return `${type}_${nodeCounter}_${Date.now()}`
}

export function useWorkflowCanvas(api, workflowId) {
    const {
        nodes,
        edges,
        addNodes,
        addEdges,
        findNode,
        onNodesChange,
        onEdgesChange,
        onConnect,
        setNodes,
        setEdges,
        screenToFlowCoordinate,
    } = useVueFlow()

    const selectedNode = ref(null)
    let saveTimer = null

    // Auto-add edges when user draws a connection
    onConnect((params) => {
        addEdges([{
            id: `e-${params.source}-${params.target}-${Date.now()}`,
            source: params.source,
            target: params.target,
            sourceHandle: params.sourceHandle,
            targetHandle: params.targetHandle,
        }])
    })

    function scheduleAutoSave() {
        if (!workflowId?.value) return
        if (saveTimer) clearTimeout(saveTimer)
        saveTimer = setTimeout(() => {
            const graphData = getGraphData()
            api.saveGraph(workflowId.value, graphData).catch(() => {})
        }, 2000)
    }

    onNodesChange(() => { scheduleAutoSave() })
    onEdgesChange(() => { scheduleAutoSave() })

    function loadGraph(graph) {
        if (!graph) return
        const loadedNodes = (graph.nodes ?? []).map(n => ({
            ...n,
            type: n.type,
        }))
        setNodes(loadedNodes)
        setEdges(graph.edges ?? [])
    }

    function getGraphData() {
        return {
            nodes: nodes.value.map(n => ({
                id: n.id,
                type: n.type,
                position: n.position,
                data: n.data,
            })),
            edges: edges.value.map(e => ({
                id: e.id,
                source: e.source,
                target: e.target,
                sourceHandle: e.sourceHandle ?? null,
                targetHandle: e.targetHandle ?? null,
            })),
        }
    }

    function addNode(type, position) {
        const id = generateNodeId(type)
        const newNode = {
            id,
            type,
            position: position ?? { x: 100, y: 100 },
            data: { config: getDefaultConfig(type) },
        }
        addNodes([newNode])
        return newNode
    }

    function updateNodeConfig(nodeId, config) {
        const node = findNode(nodeId)
        if (!node) return
        node.data = {
            ...node.data,
            config: { ...(node.data?.config ?? {}), ...config },
        }
        scheduleAutoSave()
    }

    function selectNode(nodeId) {
        const node = findNode(nodeId)
        selectedNode.value = node ?? null
    }

    function deselectNode() {
        selectedNode.value = null
    }

    return {
        nodes,
        edges,
        selectedNode,
        loadGraph,
        getGraphData,
        addNode,
        updateNodeConfig,
        selectNode,
        deselectNode,
        screenToFlowCoordinate,
    }
}
